import { shiftKeys, updateShiftTimes } from "@/api/Shifts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import styles from "./ShiftDetails.module.scss";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { useState } from "react";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import {
  shiftTimeDisplayValues,
  type IScheduledShift,
} from "@medoma/models/shifts";
import InputField from "@medoma/components/InputField/InputField";
import { machineFormat } from "@medoma/models/date-and-time";
import { useSelectedDate } from "@/Utils/useSelectedDate";

export const ChangeShiftTimes = ({ shift }: { shift: IScheduledShift }) => {
  const { t } = useLingui();
  const selectedDate = useSelectedDate();
  const shiftId = shift.id;
  const startDateTime = shift.startDateTime;
  const endDateTime = shift.endDateTime;
  const { formattedStartDay, formattedEndDay } = shiftTimeDisplayValues({
    selectedDate,
    startDateTime,
    endDateTime,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    scheduledShiftTimes: { startTime: string; endTime: string };
  }>({
    defaultValues: {
      scheduledShiftTimes: {
        startTime: machineFormat(startDateTime, "HH:mm"),
        endTime: machineFormat(endDateTime, "HH:mm"),
      },
    },
  });

  const [isChangingShiftTimes, setIsChangingShiftTimes] = useState(false);

  const queryClient = useQueryClient();

  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutate: mutateShiftTimes, isPending: isMutatingShiftTimes } =
    useMutation({
      mutationFn: ({
        startTimeLocal,
        endTimeLocal,
        shiftId,
      }: {
        startTimeLocal: string;
        endTimeLocal: string;
        shiftId: IScheduledShift["id"];
      }) => {
        return updateShiftTimes({ shiftId, startTimeLocal, endTimeLocal });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: shiftKeys.all });
        setIsChangingShiftTimes(false);
      },
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att genomföra ändringen`}. ${deducedError(error)}`,
        );
      },
    });

  return (
    <>
      <div className={styles.actions}>
        {isChangingShiftTimes ? (
          <PlainButton
            size="small"
            onClick={() => {
              setIsChangingShiftTimes(false);
            }}
            disabled={isMutatingShiftTimes}
            aria-label={t`Avbryt ändring av tider`}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
        ) : (
          <PlainButton
            size="small"
            onClick={() => {
              setIsChangingShiftTimes(true);
            }}
            disabled={isMutatingShiftTimes}
            aria-label={t`Ändra tider`}
          >
            <Trans>Ändra</Trans>
          </PlainButton>
        )}
      </div>
      {isChangingShiftTimes ? (
        <div className={styles.editContainer}>
          <Form
            onSubmit={handleSubmit(async (validatedFormData) => {
              mutateShiftTimes({
                startTimeLocal: validatedFormData.scheduledShiftTimes.startTime,
                endTimeLocal: validatedFormData.scheduledShiftTimes.endTime,
                shiftId,
              });
            })}
          >
            {isMutatingShiftTimes ? (
              <Loading message={t`Ändrar tider för arbetspasset`} />
            ) : (
              <>
                <div className={styles.changeShiftTimesField}>
                  <InputField
                    label={t`Välj starttid`}
                    {...register("scheduledShiftTimes.startTime", {
                      required: {
                        value: true,
                        message: t`Ange en starttid`,
                      },
                    })}
                    showOptionalLabel={false}
                    type="time"
                    suffix={formattedStartDay}
                    errorMessage={
                      errors.scheduledShiftTimes?.startTime?.message
                    }
                  />
                  <InputField
                    label={t`Välj sluttid`}
                    {...register("scheduledShiftTimes.endTime", {
                      required: {
                        value: true,
                        message: t`Ange en sluttid`,
                      },
                    })}
                    showOptionalLabel={false}
                    type="time"
                    suffix={formattedEndDay}
                    errorMessage={errors.scheduledShiftTimes?.endTime?.message}
                  />
                </div>
                <FilledButton disabled={isMutatingShiftTimes} type="submit">
                  <Trans>Spara ändringar</Trans>
                </FilledButton>
              </>
            )}
          </Form>
        </div>
      ) : null}
    </>
  );
};
